import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db } from '../../firebase/config';
import { ShieldCheck, AlertTriangle, X, MapPin } from 'lucide-react';

/**
 * 치과 등록 검증 대기 배너 (matchStatus 'pending_verification')
 * - "맞아요" → CF claimHospital({hospitalId, confirm:true}) — 본인 확인 시각(selfConfirmedAt)만 서버가 기록.
 *   상태는 pending_verification 유지, 등록 완료(verified_admin)는 관리자 승인(adminApproveClaim)으로만.
 *   (2026-09-15 보안 P0-1: 종전 클라이언트 updateDoc matchStatus:'verified_self' 자가 승인 — rules 에서 차단됨)
 * - "잘못 매칭됐어요" → reportClaimMismatch 호출 → claim 임시 해제
 * - 자동 검증(기간 경과 시 승인) 코드는 없다.
 */
function HospitalVerificationBanner() {
    const [hospital, setHospital] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [reportReason, setReportReason] = useState('');

    useEffect(() => { load(); }, []);

    const load = async () => {
        const uid = getAuth().currentUser?.uid;
        if (!uid) { setLoading(false); return; }
        try {
            const snap = await getDocs(query(collection(db, 'hospitals'), where('claimedBy', '==', uid)));
            if (snap.empty) { setLoading(false); return; }
            const d = snap.docs[0];
            const data = { id: d.id, ...d.data() };
            // pending_verification 이고 아직 본인 확인 전일 때만 노출
            if (data.matchStatus === 'pending_verification' && !data.selfConfirmedAt) {
                setHospital(data);
            }
        } catch (e) {
            console.error('[HospitalVerificationBanner] 조회 실패:', e?.message);
        }
        setLoading(false);
    };

    const handleConfirm = async () => {
        if (!hospital) return;
        setSubmitting(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            await httpsCallable(fns, 'claimHospital')({ hospitalId: hospital.id, confirm: true });
            alert('확인되었습니다. 관리자 확인 후 등록이 완료됩니다.');
            setHospital(null);
        } catch (e) {
            alert('확인 처리 실패: ' + e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReport = async () => {
        if (!hospital) return;
        if (!reportReason.trim()) {
            alert('잘못된 이유를 간단히 입력해주세요');
            return;
        }
        setSubmitting(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const report = httpsCallable(fns, 'reportClaimMismatch');
            await report({ hospitalId: hospital.id, reason: reportReason.trim() });
            alert('신고가 접수되었습니다. 임시로 등록이 해제되며, 다시 검색하실 수 있습니다.');
            setHospital(null);
            window.location.reload();
        } catch (e) {
            alert('신고 실패: ' + e.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || !hospital) return null;

    return (
        <div style={S.wrapper}>
            <div style={S.banner}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1 }}>
                    <ShieldCheck size={20} color="#0369a1" />
                    <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <strong style={{ fontSize: 13, color: '#0c4a6e' }}>
                                치과 등록 확인 대기 중
                            </strong>
                            <span style={{
                                padding: '2px 6px', background: '#fef3c7', color: '#92400e',
                                borderRadius: 3, fontSize: 9, fontWeight: 800,
                            }}>
                                정확도 {hospital.matchScore || 0}%
                            </span>
                        </div>
                        <div style={{ fontSize: 11, color: '#075985', marginTop: 4, lineHeight: 1.5 }}>
                            <MapPin size={11} style={{ display: 'inline', verticalAlign: 'middle' }} />{' '}
                            <strong>{hospital.name}</strong> · {hospital.address}
                        </div>
                        <div style={{ fontSize: 10, color: '#0369a1', marginTop: 4 }}>
                            본인 치과가 맞으신가요? 확인해 주시면 관리자 확인 후 등록이 완료됩니다.
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button
                        onClick={handleConfirm}
                        disabled={submitting}
                        style={S.confirmBtn}
                    >
                        ✓ 맞아요
                    </button>
                    <button
                        onClick={() => setShowReport(true)}
                        style={S.reportBtn}
                    >
                        잘못 매칭됐어요
                    </button>
                </div>
            </div>

            {/* 신고 모달 */}
            {showReport && (
                <div style={S.overlay} onClick={() => setShowReport(false)}>
                    <div style={S.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={S.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>
                                <AlertTriangle size={16} color="#ef4444" style={{ verticalAlign: 'middle' }} /> 잘못된 매칭 신고
                            </h3>
                            <button onClick={() => setShowReport(false)} style={S.closeBtn}><X size={18} /></button>
                        </div>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '12px 0', lineHeight: 1.6 }}>
                            <strong>{hospital.name}</strong>가 본인 치과가 아니라면 신고해주세요.
                            <br />신고 즉시 매칭이 임시 해제되며, 다시 검색·등록하실 수 있습니다.
                        </p>
                        <textarea
                            value={reportReason}
                            onChange={(e) => setReportReason(e.target.value)}
                            placeholder="예: 같은 이름이지만 다른 치과입니다 / 이미 폐업한 치과입니다"
                            rows={3}
                            style={S.textarea}
                            maxLength={500}
                        />
                        <div style={S.modalFooter}>
                            <button onClick={() => setShowReport(false)} style={S.cancelBtn}>취소</button>
                            <button
                                onClick={handleReport}
                                disabled={submitting || !reportReason.trim()}
                                style={{ ...S.submitBtn, opacity: (!reportReason.trim() || submitting) ? 0.5 : 1 }}
                            >
                                {submitting ? '신고 중...' : '신고 접수'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    wrapper: { padding: '12px 20px 0' },
    banner: {
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '12px 14px',
        background: 'linear-gradient(135deg, #eff6ff, #dbeafe)',
        border: '1px solid #93c5fd', borderRadius: 10,
    },
    confirmBtn: {
        padding: '8px 12px', background: '#10b981', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 800, cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    reportBtn: {
        padding: '8px 12px', background: 'transparent', color: '#ef4444',
        border: '1px solid #ef4444', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
        whiteSpace: 'nowrap',
    },
    overlay: {
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, padding: 20,
    },
    modalHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 },
    textarea: {
        width: '100%', padding: 10, border: '1px solid #cbd5e1',
        borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
        boxSizing: 'border-box',
    },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 },
    cancelBtn: {
        padding: '8px 14px', background: 'transparent', color: '#64748b',
        border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
    submitBtn: {
        padding: '8px 14px', background: '#ef4444', color: '#fff',
        border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    },
};

export default HospitalVerificationBanner;
